import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { initialState } from './initialState';
import {
  IOffersHistoryDefaultFilters,
  ISuccessOfferFromHistoryPayload,
  TCategoriesFilterActiveValue,
  TRoomsFilterOptionValue,
  TStatusFilterOptionValue,
  TTypeFilterActiveValue,
} from 'shared/types/offersHistory';
import { FILTER_ALL_VALUE } from 'shared/constants/offersHistory';
import { IGetOffersHistoryResult, getOfferFromHistory, getOffersHistory } from 'shared/thunks/offersHistory';

const offersHistorySlice = createSlice({
  initialState,
  name: 'offersHistory',
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setRoomsFilter: (state, action: PayloadAction<TRoomsFilterOptionValue>) => {
      state.roomsFilterActiveValue = action.payload;
      state.isFiltersChanged = true;
    },
    setStatusFilter: (state, action: PayloadAction<TStatusFilterOptionValue>) => {
      state.statusFilterActiveValue = action.payload;
      state.isFiltersChanged = true;
    },
    setTypeFilter: (state, action: PayloadAction<TTypeFilterActiveValue>) => {
      state.typeFilterActiveValue = action.payload;
      state.isFiltersChanged = true;
    },
    setCategoriesFilter: (state, action: PayloadAction<TCategoriesFilterActiveValue>) => {
      state.categoriesFilterActiveValue = action.payload;
      state.isFiltersChanged = true;
    },
    setActiveOfferId: (state, action: PayloadAction<number | null>) => {
      state.activeOfferId = action.payload;
    },
    setIsFiltersChanged: (state, action: PayloadAction<boolean>) => {
      state.isFiltersChanged = action.payload;
    },
    setViewedOffer: (state, action: PayloadAction<number>) => {
      state.viewedOffers = state.viewedOffers.includes(action.payload)
        ? state.viewedOffers
        : state.viewedOffers.concat(action.payload);
    },
    resetFilters: state => {
      state.roomsFilterActiveValue = initialState.roomsFilterActiveValue;
      state.statusFilterActiveValue = initialState.statusFilterActiveValue;
      state.typeFilterActiveValue = initialState.typeFilterActiveValue;
      state.page = initialState.page;
      state.isFiltersChanged = initialState.isFiltersChanged;
    },
    resetListingData: state => {
      state.data.listing = initialState.data.listing;
    },
    saveDefaultFilters: (state, action: PayloadAction<Partial<IOffersHistoryDefaultFilters>>) => {
      state.defaultFilters = {
        ...state.defaultFilters,
        ...action.payload,
      };
    },
    setOffersHistoryLoadMore: (state, action: PayloadAction<boolean>) => {
      state.data.listing.isLoadMore = action.payload;
    },
    setDefaultRooms: state => {
      state.roomsFilterActiveValue = state.defaultFilters.roomCounts;
    },
    setDefaultCategory: state => {
      state.categoriesFilterActiveValue = FILTER_ALL_VALUE;
    },
    setNextPage: state => {
      state.page += 1;
    },
    resetOffersHistoryListingState: state => {
      state.data.listing.isError = false;
      state.data.listing.isFetching = false;
      state.data.listing.isAuthError = false;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getOfferFromHistory.pending, state => {
        state.data.offers.isFetching = true;
        state.data.offers.isError = false;
      })
      .addCase(getOfferFromHistory.fulfilled, (state, action: PayloadAction<ISuccessOfferFromHistoryPayload>) => {
        state.data.offers.isFetching = false;
        state.data.offers.isError = false;
        state.data.offers.isAuthError = false;
        state.data.offers.data = {
          ...state.data.offers.data,
          [action.payload.id]: action.payload.data,
        };
      })
      .addCase(getOfferFromHistory.rejected, (state, action) => {
        /* istanbul ignore else */
        if (action.payload) {
          const { isAuthError = false, isError = false } = action.payload;

          state.data.offers.isFetching = false;
          state.data.offers.isError = isError;
          state.data.offers.isAuthError = isAuthError;
        }
      })
      .addCase(getOffersHistory.pending, state => {
        state.data.listing.isFetching = true;
        state.data.listing.isError = false;
      })
      .addCase(getOffersHistory.fulfilled, (state, action: PayloadAction<IGetOffersHistoryResult>) => {
        const { data, isLoadMore } = action.payload;

        if (isLoadMore) {
          const prevOffers = state.data.listing.data?.offers || /* istanbul ignore next */ [];

          state.data.listing.data = {
            ...data,
            offers: prevOffers.concat(data.offers),
          };
        } else {
          state.data.listing.data = data;
        }

        state.data.listing.isFetching = false;
        state.data.listing.isError = false;
        state.data.listing.isLoadMore = false;
      })
      .addCase(getOffersHistory.rejected, state => {
        state.data.listing.isFetching = false;
        state.data.listing.isLoadMore = false;
        state.data.listing.isError = true;
      });
  },
});

export const {
  reducer: offersHistoryReducer,
  actions: {
    setActiveOfferId,
    setCategoriesFilter,
    setIsFiltersChanged,
    setOffersHistoryLoadMore,
    setPage,
    setRoomsFilter,
    setStatusFilter,
    setTypeFilter,
    setViewedOffer,
    saveDefaultFilters,
    resetFilters,
    resetListingData,
    setDefaultRooms,
    setDefaultCategory,
    setNextPage,
    resetOffersHistoryListingState,
  },
} = offersHistorySlice;
